import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usePrivacy } from '../../context/PrivacyContext';
import GlobalSearch from '../search/GlobalSearch';
import './Sidebar.css';

const COLLAPSED_W = '72px';
const EXPANDED_W  = '240px';

/* ── Nav data ───────────────────────────────────────────────────────── */
const NAV = [
  { type: 'link', label: 'Dashboard',   icon: 'bi-house-door-fill', to: '/', exact: true },
  { type: 'link', label: 'Analysis',    icon: 'bi-graph-up-arrow',  to: '/analysis' },
  { type: 'link', label: 'Leaderboard', icon: 'bi-trophy-fill',     to: '/leaderboard' },
  { type: 'link', label: 'Calendar',    icon: 'bi-calendar3',       to: '/calendar' },
  { type: 'link', label: 'Activity',    icon: 'bi-clock-history',   to: '/activity' },
  { type: 'divider' },
  { type: 'section', label: 'Management' },
  {
    type: 'group', label: 'Accounts', icon: 'bi-people-fill', prefix: '/account',
    children: [
      { label: 'Create Account', to: '/account/create' },
      { label: 'View Accounts', to: '/accounts' },
    ],
  },
  {
    type: 'group', label: 'Subscriptions', icon: 'bi-bookmark-star-fill', prefix: '/subscription',
    children: [
      { label: 'Create Subscription', to: '/subscription/create' },
      { label: 'View Subscriptions', to: '/subscriptions' },
      { label: 'Inactive', to: '/subscription/inactive' },
      { label: 'Awaiting Renewal', to: '/subscription/renewals', badgeKey: 'expiredSubs' },
    ],
  },
  {
    type: 'group', label: 'App Services', icon: 'bi-phone-fill', prefix: '/appservices',
    children: [
      { label: 'View App Subscriptions', to: '/appservices/subscriptions' },
    ],
  },
  {
    type: 'group', label: 'Transactions', icon: 'bi-receipt', prefix: '/transaction',
    children: [
      { label: 'Create Transaction', to: '/transaction/create' },
      { label: 'Log Expense', to: '/transaction/expense/create' },
      { label: 'View Transactions', to: '/transactions' },
    ],
  },
  {
    type: 'group', label: 'Referrals', icon: 'bi-share-fill', prefix: '/referral',
    children: [
      { label: 'Create Referral', to: '/referral/create' },
      { label: 'View Referrals', to: '/referrals' },
      { label: 'Trial Follow-ups', to: '/referrals/trial-followups' },
    ],
  },
  {
    type: 'group', label: 'Resellers', icon: 'bi-person-badge-fill', prefix: '/reseller',
    children: [
      { label: 'Log Credit Purchase', to: '/reseller/credits/create' },
      { label: 'View Resellers', to: '/resellers' },
      { label: 'Credit Cost Rates', to: '/reseller/rates' },
    ],
  },
  {
    type: 'group', label: 'Wallets', icon: 'bi-wallet2', prefix: '/wallet',
    children: [
      { label: 'View Wallets', to: '/wallets' },
      { label: 'Invoices', to: '/invoices' },
    ],
  },
  {
    type: 'group', label: 'Points', icon: 'bi-star-fill', prefix: '/points',
    children: [
      { label: 'Add Points', to: '/points/add' },
      { label: 'View Points', to: '/points' },
    ],
  },
  { type: 'divider' },
  { type: 'section', label: 'Server Data' },
  { type: 'link', label: 'Bot Requests',   icon: 'bi-inbox-fill',              to: '/serverdata/requests', badgeKey: 'pendingRequests' },
  { type: 'link', label: 'Issues',         icon: 'bi-exclamation-triangle-fill', to: '/serverdata/issues' },
  { type: 'link', label: 'Reminders',      icon: 'bi-alarm-fill',              to: '/serverdata/reminders' },
  { type: 'link', label: 'Checklist',      icon: 'bi-list-check',              to: '/serverdata/checklist', badgeKey: 'overdueChecklist' },
  { type: 'divider' },
  { type: 'section', label: 'Server Management' },
  {
    type: 'group', label: 'Announcements', icon: 'bi-megaphone-fill', prefix: '/servermanagement',
    children: [
      { label: 'Send Announcement', to: '/servermanagement/announcements' },
    ],
  },
];

/* ── Badge pill ─────────────────────────────────────────────────────── */
function NavBadge({ count, collapsed }) {
  if (!count) return null;
  if (collapsed) return <span className="nav-badge-dot" />;
  return <span className="nav-badge">{count > 99 ? '99+' : count}</span>;
}

/* ── NavGroup ───────────────────────────────────────────────────────── */
function NavGroup({ item, pathname, badges, collapsed }) {
  const isChildActive = item.children.some(c => pathname === c.to);
  const [open, setOpen] = useState(isChildActive);
  const groupBadgeTotal = item.children.reduce((sum, c) => sum + (c.badgeKey ? (badges[c.badgeKey] || 0) : 0), 0);

  return (
    <div className="nav-group">
      <button
        type="button"
        className={`nav-group-toggle${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        title={collapsed ? item.label : undefined}
      >
        <span className="nav-icon"><i className={`bi ${item.icon}`} /></span>
        <span className="nav-label">{item.label}</span>
        <NavBadge count={groupBadgeTotal} collapsed={collapsed} />
        <i className="bi bi-chevron-right nav-chevron" />
      </button>

      <div className={`nav-children${open ? ' open' : ''}`}>
        {item.children.map(child => (
          <Link
            key={child.to}
            to={child.to}
            className={`nav-child${pathname === child.to ? ' active' : ''}`}
          >
            <span>{child.label}</span>
            {child.badgeKey && <NavBadge count={badges[child.badgeKey]} />}
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Sidebar ────────────────────────────────────────────────────────── */
export default function Sidebar({ title }) {
  const { pathname } = useLocation();
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { dark, toggle: toggleTheme } = useTheme();
  const { hidden: privacyHidden, toggle: togglePrivacy } = usePrivacy();
  const [collapsed, setCollapsed]   = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [badges, setBadges]         = useState({ expiredSubs: 0, pendingRequests: 0, overdueChecklist: 0 });
  const profileRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  /* ── Apply persistent rail width as a CSS var body reads for its left offset ── */
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', collapsed ? COLLAPSED_W : EXPANDED_W);
  }, [collapsed]);

  /* ── Nav badge counts (expired subs awaiting renewal, pending bot requests, overdue checklist items) ── */
  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/subscription/expired`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${process.env.REACT_APP_API_URL}/botdata/requests?status=pending`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${process.env.REACT_APP_API_URL}/botdata/checklist?status=open`, { headers }).catch(() => ({ data: [] })),
    ]).then(([expiredRes, pendingRes, checklistRes]) => {
      const items = Array.isArray(checklistRes.data) ? checklistRes.data : [];
      const now = Date.now();
      setBadges({
        expiredSubs: Array.isArray(expiredRes.data) ? expiredRes.data.length : 0,
        pendingRequests: Array.isArray(pendingRes.data) ? pendingRes.data.length : 0,
        overdueChecklist: items.filter(i => i.dueAt && new Date(i.dueAt).getTime() <= now).length,
      });
    });
  }, []);

  /* ── Ctrl+K / Cmd+K global shortcut, Escape closes mobile drawer ── */
  useEffect(() => {
    const handler = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ── Close mobile drawer on route change ── */
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  /* ── Close profile menu on outside click ── */
  useEffect(() => {
    if (!profileOpen) return;
    const handler = e => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [profileOpen]);

  return (
    <>
      {/* ── Top bar ── */}
      <header className="dash-navbar">
        <div className="dash-brand">
          <button
            className="dash-hamburger"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            title={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`bi ${mobileOpen ? 'bi-x-lg' : 'bi-list'}`} />
          </button>
          <span className="brand-title">{title}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="dash-search-btn" onClick={() => setSearchOpen(true)} aria-label="Search" title="Search (Ctrl+K)">
            <i className="bi bi-search" />
          </button>
          <button
            className="dash-theme-btn"
            onClick={toggleTheme}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={dark ? 'Light mode' : 'Dark mode'}
          >
            <i className={`bi ${dark ? 'bi-sun-fill' : 'bi-moon-fill'}`} />
          </button>
          <button
            className="dash-privacy-btn"
            onClick={togglePrivacy}
            aria-label={privacyHidden ? 'Show sensitive info' : 'Hide sensitive info'}
            title={privacyHidden ? 'Show sensitive info' : 'Hide sensitive info'}
          >
            <i className={`bi ${privacyHidden ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`} />
          </button>

          {/* ── Profile menu ── */}
          <div className="dash-profile" ref={profileRef}>
            <button className="dash-profile-btn" onClick={() => setProfileOpen(o => !o)} aria-label="Account menu">
              <span className="dash-profile-avatar"><i className="bi bi-person-fill" /></span>
            </button>
            {profileOpen && (
              <div className="dash-profile-menu">
                <div className="dash-profile-menu-header">
                  <span className="dash-profile-avatar" style={{ width: 34, height: 34 }}><i className="bi bi-person-fill" /></span>
                  <span className="dash-profile-name">{user?.username || 'Account'}</span>
                </div>
                <div className="nav-divider" style={{ margin: '6px 0' }} />
                <button className="dash-profile-menu-item logout" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right" /> Logout
                </button>
              </div>
            )}
          </div>

          <button className="dash-toggle" onClick={toggleCollapsed} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} title={collapsed ? 'Expand menu' : 'Collapse menu'}>
            <i className={`bi ${collapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'}`} />
          </button>
        </div>
      </header>

      {/* ── Global search portal ── */}
      {searchOpen && createPortal(
        <GlobalSearch onClose={() => setSearchOpen(false)} />,
        document.body
      )}

      {/* ── Mobile backdrop ── */}
      {mobileOpen && <div className="dash-backdrop" onClick={() => setMobileOpen(false)} />}

      {/* ── Persistent side rail ── */}
      <aside className={`dash-panel${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>

        {/* Panel header */}
        <div className="panel-hdr">
          <div className="panel-hdr-brand">
            {!collapsed && <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-text)' }}>Business Dashboard</span>}
          </div>
        </div>

        {/* Nav items */}
        <nav className="panel-nav">
          {NAV.map((item, i) => {
            if (item.type === 'divider') return <div key={i} className="nav-divider" />;
            if (item.type === 'section') return collapsed ? null : <div key={i} className="nav-section-label">{item.label}</div>;

            if (item.type === 'link') {
              const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
              return (
                <Link key={i} to={item.to} className={`nav-single${active ? ' active' : ''}`} title={collapsed ? item.label : undefined}>
                  <span className="nav-icon"><i className={`bi ${item.icon}`} /></span>
                  <span className="nav-label">{item.label}</span>
                  {item.badgeKey && <NavBadge count={badges[item.badgeKey]} collapsed={collapsed} />}
                </Link>
              );
            }

            if (item.type === 'group') {
              return <NavGroup key={i} item={item} pathname={pathname} badges={badges} collapsed={collapsed} />;
            }

            return null;
          })}

          <div className="nav-divider" style={{ marginTop: 16 }} />

          <button className="nav-logout" onClick={handleLogout} title={collapsed ? 'Logout' : undefined}>
            <span className="nav-icon"><i className="bi bi-box-arrow-right" /></span>
            <span className="nav-label">Logout</span>
          </button>
        </nav>
      </aside>
    </>
  );
}
